import React, { useEffect, useState, memo } from 'react';
import { EChart as ReactECharts } from '../EChart';
import { Card } from 'react-bootstrap';
import * as echarts from 'echarts/core';
import dayjs from 'dayjs';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  ToolboxComponent,
  DataZoomComponent
} from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import axios from 'axios';
import { isLiveTimeRange } from '../utils/chartUtils';
import { INFLUX_API } from '../../../config/api';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent,
  ToolboxComponent,
  DataZoomComponent
]);

// N series on one axis. Named for its first use — the extruder temperature
// zones — but it is the only multi-series chart here, so `title` and `unit` are
// props now: pointed at a traverse's pulse limits it was captioning them
// "DTB1250 Temp Zones" and labelling the axis in °C.
const MachineTempZonesChart = ({
  bucket,
  machineName,
  fields,
  startTime,
  endTime,
  title,
  unit = '°C'
}) => {
  const [seriesData, setSeriesData] = useState([]);
  const [yAxisMin, setYAxisMin] = useState(0); // Y-axis minimum
  const [yAxisMax, setYAxisMax] = useState(0); // Y-axis maximum

  // Fetch data based on machineName, fields, and period
  const fetchData = async () => {
    try {
      const response = await axios.get(
        `${INFLUX_API}/api/influx/${machineName}/fields?fields=${fields}&start=${startTime}&end=${endTime}&bucket=${bucket}`
      );
      const data = response.data;

      // Sort each key's data by timestamp (first value in each array)
      const sortedData = Object.keys(data).reduce((acc, key) => {
        const sortedValues = data[key].sort(
          (a, b) => new Date(a[0]) - new Date(b[0])
        );
        acc[key] = sortedValues;
        return acc;
      }, {});

      // Extract all temperature values and calculate min and max
      const allValues = Object.keys(sortedData).reduce((acc, key) => {
        return acc.concat(sortedData[key].map(item => item[1])); // Collect all y-axis values
      }, []);

      const minVal = Math.min(...allValues);
      const maxVal = Math.max(...allValues);

      // Add padding to the min and max values
      const minPadding = Math.floor(minVal - 10); // 10 degrees lower
      const maxPadding = Math.ceil(maxVal + 10); // 10 degrees higher

      setYAxisMin(minPadding);
      setYAxisMax(maxPadding);

      // Map the sorted data to create dynamic series based on the fields in the response
      const dynamicSeries = Object.keys(sortedData).map((key, index) => ({
        name: key, // Use the key as the series name (e.g., E120_J_ACT_TEMP_Z1)
        type: 'line',
        smooth: true, // Smooth lines
        showSymbol: false,
        data: sortedData[key], // Use the sorted data for that key
        lineStyle: {
          width: 1
        },
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: `rgb(${(index * 30) % 255}, ${(index * 50) % 255}, ${
                (index * 70) % 255
              })` // Dynamic color
            },
            {
              offset: 1,
              color: `rgb(${(index * 50) % 255}, ${(index * 20) % 255}, ${
                (index * 90) % 255
              })`
            }
          ])
        }
      }));

      setSeriesData(dynamicSeries);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Use effect to fetch data whenever machineName, fields, or period changes
  useEffect(() => {
    fetchData();
    const intervalId = isLiveTimeRange(endTime) ? setInterval(fetchData, 60000) : null; // Fetch every 5 seconds
    return () => clearInterval(intervalId); // Clear interval on component unmount
  }, [machineName, fields, startTime, endTime]);

  const option = {
    title: {
      text: title || `${machineName} Temp Zones`
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985'
        }
      }
    },
    toolbox: {
      feature: {
        saveAsImage: {}
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: [
      {
        type: 'time',
        boundaryGap: false,
        axisLabel: {
          formatter: value => dayjs(value).format('HH:mm'),
          interval: 'auto',
          margin: 10
        },
        splitLine: {
          show: false
        }
      }
    ],
    yAxis: [
      {
        type: 'value',
        min: yAxisMin, // Use dynamic min value for the y-axis
        max: yAxisMax, // Use dynamic max value for the y-axis
        axisLabel: {
          formatter: `{value} ${unit}`
        },
        splitLine: {
          show: false
        }
      }
    ],
    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: 0,
        start: 0,
        end: 100
      }
    ],
    series: seriesData // Use dynamic series data
  };

  return (
    <Card className="h-100">
      <Card.Body>
        <ReactECharts
          option={option}
          style={{ height: '400px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

export default memo(MachineTempZonesChart);
